import React, { useState } from "react";
import {
  Abc,
  CategoryOutlined,
  Close,
  HelpOutline,
  RateReviewOutlined,
  SlowMotionVideoOutlined,
} from "@mui/icons-material";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { MdDragIndicator } from "react-icons/md";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SalesAidsStyles } from "./SalesAidsStyles";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";

// Row1: N U CPO O  |  Row2: F L C B
const ROW1 = ["N", "U", "CPO", "O"];
const ROW2 = ["F", "L", "C", "B"];
const ALL_KEYS = [...ROW1, ...ROW2];

const buildDefaultBubbles = () =>
  ALL_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {});

// ── Tiny 2×4 bubble grid ─────────────────────────────────────────────────────
const BubbleGrid = ({ bubbles, onChange }) => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: "7px", flexShrink: 0 }}
  >
    {[ROW1, ROW2].map((row, ri) => (
      <Box key={ri} sx={{ display: "flex", gap: "7px" }}>
        {row.map((key) => (
          <Box
            key={key}
            onClick={() => onChange(key)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              border: "1.5px solid #4caf50",
              backgroundColor: bubbles[key] ? "#4caf50" : "#fff",
              cursor: "pointer",
              transition: "background-color 0.15s",
              flexShrink: 0,
            }}
          />
        ))}
      </Box>
    ))}
  </Box>
);

// ── Bubble column header labels ──────────────────────────────────────────────
const BubbleHeader = () => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: "7px", flexShrink: 0 }}
  >
    {[ROW1, ROW2].map((row, ri) => (
      <Box key={ri} sx={{ display: "flex", gap: "7px" }}>
        {row.map((key) => (
          <Typography
            key={key}
            sx={{
              width: 10,
              fontSize: "7px",
              lineHeight: 1,
              fontWeight: "bold",
              textAlign: "center",
              color: "black",
              // fontWeight: 600,
              userSelect: "none",
            }}
          >
            {key}
          </Typography>
        ))}
      </Box>
    ))}
  </Box>
);

// ── Sortable row ─────────────────────────────────────────────────────────────
const SortableItem = ({
  item,
  index,
  onRemove,
  hoverIndex,
  setHoverIndex,
  bubbles,
  onBubbleChange,
  showBubbles,
}) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.artifactId || index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const ContentDisplay = ({ content, tooltip }) => (
    <span
      onMouseEnter={() => setHoverIndex(index)}
      onMouseLeave={() => setHoverIndex(null)}
      style={{ fontSize: "12px" }}
    >
      {tooltip && tooltip.length > 50 ? (
        <Tooltip title={tooltip}>{tooltip.substring(0, 50) + "..."}</Tooltip>
      ) : (
        content
      )}
    </span>
  );

  return (
    <Box ref={setNodeRef} style={style}>
      <Box sx={{ ...SalesAidsStyles.orderInsideBox, alignItems: "center" }}>
        {/* Left icon or delete */}
        <Box
          sx={{ width: "10%", flexShrink: 0 }}
          onMouseEnter={() => setHoverIndex(index)}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {hoverIndex === index ? (
            <Close
              sx={SalesAidsStyles.arraowBack}
              onClick={() => onRemove(index)}
            />
          ) : (
            <Box>
              {(item?.text || item?.es_text) && (
                <Abc sx={{ fontSize: "19px" }} />
              )}
              {(item?.question || item?.es_question) && (
                <HelpOutline sx={{ fontSize: "19px" }} />
              )}
              {(item?.title || item?.es_title) &&
                (item?.url || item?.es_url) && (
                  <SlowMotionVideoOutlined sx={{ fontSize: "19px" }} />
                )}
              {(item?.review_question || item?.es_review_question) &&
                (item?.second_review_question ||
                  item?.es_second_review_question) &&
                (item?.referral_question || item?.es_referral_question) && (
                  <RateReviewOutlined sx={{ fontSize: "18px" }} />
                )}
              {(item?.message || item?.es_message) &&
                (item?.appstore || item?.playstore) && (
                  <CategoryOutlined sx={{ fontSize: "18px" }} />
                )}
              {(item?.message || item?.es_message) &&
                (item?.preference_options?.length > 0 ||
                  item?.es_preference_options?.length > 0) && (
                  <CategoryOutlined sx={{ fontSize: "18px" }} />
                )}
              {(item?.vehicle_information || item?.es_vehicle_information) && (
                <CategoryOutlined sx={{ fontSize: "18px" }} />
              )}
{item?.is_enabled && (
  <Abc sx={{ fontSize: "19px" }} />
)}
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ ...SalesAidsStyles.orderContentBox, flex: 1 }}>
          <Typography variant="body1" sx={{ fontSize: "13px" }}>
            {item?.text || item?.es_text
              ? "Text"
              : item?.question || item?.es_question
                ? "Survey"
                : item?.url || item?.es_url
                  ? "Video"
                  : item?.review_question ||
                      item?.second_review_question ||
                      item?.referral_question ||
                      item?.es_review_question ||
                      item?.es_second_review_question ||
                      item?.es_referral_question
                    ? "Review"
                    : item?.appstore || item?.playstore
                      ? "Dealer Engagement"
                      : item?.preference_options?.length > 0 ||
                          item?.es_preference_options?.length > 0
                        ? "Appointment"
                        : item?.vehicle_information ||
                            item?.es_vehicle_information
                          ? "Vehicle and Transaction Type"
                          : item?.is_enabled
    ? "Warranty"
                          : ""}
          </Typography>
          {(item?.vehicle_information || item?.es_vehicle_information) && (
            <ContentDisplay
              content={[item?.vehicle_information, item?.es_vehicle_information]
                .filter(Boolean)
                .join(", ")}
              tooltip={[item?.vehicle_information, item?.es_vehicle_information]
                .filter(Boolean)
                .join(", ")}
            />
          )}
          {(item?.text || item?.es_text) && (
            <ContentDisplay
              content={[item?.text, item?.es_text].filter(Boolean).join(", ")}
              tooltip={[item?.text, item?.es_text].filter(Boolean).join(", ")}
            />
          )}
          {(item?.question || item?.es_question) && (
            <ContentDisplay
              content={[item?.question, item?.es_question]
                .filter(Boolean)
                .join(", ")}
              tooltip={[item?.question, item?.es_question]
                .filter(Boolean)
                .join(", ")}
            />
          )}
{item?.is_enabled && (
  <ContentDisplay content="Warranty module enabled" tooltip="Warranty module enabled" />
)}
          {(item?.url || item?.es_url) && (
            <ContentDisplay
              content={item.url || item.es_url}
              tooltip={`${item.title || item.es_title}, ${item.url || item.es_url}`}
            />
          )}
          {(item?.dealer_rater ||
            item?.facebook ||
            item?.google ||
            item?.review_question ||
            item?.second_review_question ||
            item?.referral_question ||
            item?.es_review_question ||
            item?.es_second_review_question ||
            item?.es_referral_question) && (
            <ContentDisplay
              content={
                item?.dealer_rater ||
                item?.facebook ||
                item?.google ||
                item?.review_question ||
                item?.second_review_question ||
                item?.referral_question
              }
              tooltip={`${item?.message || ""} ${item?.dealer_rater || ""} ${item?.facebook || ""} ${item?.google || ""} ${item?.review_question || ""} ${item?.second_review_question || ""} ${item?.referral_question || ""}`}
            />
          )}
          {(item?.appstore || item?.playstore) && (
            <ContentDisplay
              content={item?.appstore || item?.playstore}
              tooltip={`${item?.message || ""} ${item?.appstore || ""} ${item?.playstore || ""}`}
            />
          )}
          {(item?.preference_options?.length > 0 ||
            item?.es_preference_options?.length > 0) && (
            <ContentDisplay
              content={item?.message || item?.es_message}
              tooltip={`${item?.message || ""},${item?.es_message || ""},${
                item?.preference_options?.join(", ") ||
                item?.es_preference_options?.join(", ")
              }`}
            />
          )}
        </Box>

        {/* Drag handle */}
        <IconButton
          {...listeners}
          {...attributes}
          sx={{ cursor: "grab", ml: "4px" }}
        >
          <MdDragIndicator />
        </IconButton>

        {/* 2×4 tiny bubble grid — after drag handle */}
        {showBubbles ? (
          <BubbleGrid bubbles={bubbles} onChange={onBubbleChange} />
        ) : (
          <Box sx={{ width: 61 }} /> // maintain spacing
        )}
      </Box>
      <Divider sx={{ color: "black", width: "100%" }} />
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const OrderComponent = ({ order, setOrder, bubbleStates, setBubbleStates }) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  // const [bubbleStates, setBubbleStates] = useState(() =>
  //   order.reduce((acc, item, idx) => {
  //     acc[item.artifactId || idx] = buildDefaultBubbles();
  //     return acc;
  //   }, {})
  // );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (active?.id && over?.id && active.id !== over.id) {
      const oldIndex = order.findIndex(
        (i, idx) => (i.artifactId || idx) === active.id,
      );
      const newIndex = order.findIndex(
        (i, idx) => (i.artifactId || idx) === over.id,
      );
      setOrder((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleRemove = (index) => setOrder(order.filter((_, i) => i !== index));

  const handleBubbleChange = (itemKey, bubbleKey) => {
    setBubbleStates((prev) => {
      const currentBubbles = prev[itemKey] || buildDefaultBubbles();
      return {
        ...prev,
        [itemKey]: {
          ...currentBubbles,
          [bubbleKey]: !currentBubbles[bubbleKey],
        },
      };
    });
  };

  const vttIndex = order.findIndex(
    (item) =>
      item.artifact_type_id === 17 ||
      item.vehicle_information ||
      item.es_vehicle_information,
  );

  return (
    <Box sx={SalesAidsStyles.executionBox}>
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Execution Sequence</Typography>
        {vttIndex !== -1 && <BubbleHeader />}
      </Box>

      <Divider sx={{ color: "black", width: "100%" }} />

      <Box sx={SalesAidsStyles.orderOutside}>
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={order.map((item, index) => item.artifactId || index)}
            strategy={verticalListSortingStrategy}
          >
            {order.map((item, index) => {
              const itemKey = item.artifactId || index;
              const showBubbles = vttIndex !== -1 && index > vttIndex;
              return (
                <SortableItem
                  key={itemKey}
                  item={item}
                  index={index}
                  onRemove={handleRemove}
                  hoverIndex={hoverIndex}
                  setHoverIndex={setHoverIndex}
                  bubbles={
                    bubbleStates[item.artifactId] ||
                    bubbleStates[item.id] ||
                    buildDefaultBubbles()
                  }
                  onBubbleChange={(bubbleKey) =>
                    handleBubbleChange(item.artifactId || itemKey, bubbleKey)
                  }
                  showBubbles={showBubbles}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  );
};

export default OrderComponent;
